import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Users, MessageSquare, ArrowRight, UserPlus, Sparkles } from "lucide-react";
import AgencyLinkInbox from "../../components/agency/AgencyLinkInbox";

export default function AgentDashboard() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    api.get("/conversations/inbox/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    api.get("/agency/roster").then(({ data }) => setRoster(data || [])).catch(() => {});
  }, []);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">Agency workspace</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="agent-dashboard-title">
            {user?.full_name || "Your agency"}
          </h1>
          <p className="text-ink-muted mt-2 text-sm">Manage your roster and run sponsorship outreach for every athlete from one workspace.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-10">
          <Stat label="Roster" value={roster.length} sub="athletes managed" testid="agent-stat-roster" />
          <Stat label="Partnership inbox" value={unread} sub="unread brand asks" testid="agent-stat-inbox" />
          <Stat label="Plan" value={user?.plan || "free"} sub="upgrade for unlimited seats" testid="agent-stat-plan" />
        </div>

        <AgencyLinkInbox />

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <Card title="Manage your roster" desc="Add athletes, build their profiles, run outreach on their behalf — even before they join." to="/app/athletes" icon={Users} testid="card-roster" />
          <Card title="Partnership inbox" desc="Brand asks flow directly into your inbox. Accept, decline or negotiate on behalf of your athletes." to="/app/inbox" icon={MessageSquare} testid="card-agent-inbox" />
        </div>

        <div className="surface p-6 border-volt/30" data-testid="agent-claim-explainer">
          <div className="flex items-start gap-4">
            <div className="bg-volt/10 p-3"><Sparkles className="w-5 h-5 text-volt" /></div>
            <div className="flex-1">
              <div className="font-heading text-xl uppercase">Athletes can claim their profile anytime</div>
              <p className="text-ink-muted text-sm mt-1">
                Every athlete you add gets their own PITCHD profile. They can claim it via email invite — once claimed, the profile becomes a shared workspace where you both collaborate.
              </p>
              <Link to="/app/athletes" className="btn-volt inline-flex items-center gap-2 mt-4 text-sm" data-testid="add-first-athlete-btn">
                <UserPlus className="w-4 h-4" /> Add your first athlete
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="label-eyebrow text-ink-muted">{label}</div>
      <div className="font-heading text-4xl mt-2 uppercase">{value}</div>
      <div className="text-xs text-ink-muted mt-1">{sub}</div>
    </div>
  );
}

function Card({ title, desc, to, icon: Icon, testid }) {
  return (
    <Link to={to} className="surface p-6 hover:border-volt transition-colors group" data-testid={testid}>
      <Icon className="w-6 h-6 text-volt mb-3" />
      <div className="font-heading text-xl uppercase tracking-tight">{title}</div>
      <p className="text-ink-muted text-sm mt-2">{desc}</p>
      <div className="text-volt text-xs uppercase tracking-widest mt-4 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
        Open <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}
